import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { styled } from "@linaria/react";
import { t } from "@/themes/theme.ts";
import { MessageSide } from "@/types/message.ts";
import { type FlowGeneratorSession, FlowItemType } from "@/types/flow.ts";
import { FlowMessage } from "@/components/flowItems/FlowMessage.tsx";
import type { Chat } from "@/stores/useChatStore.ts";
import { AnimatePresence, motion } from "motion/react";
import type { AssignedSession } from "@/stores/useAssignedChatStore.ts";
import { useInputStore } from "@/stores/useInputStore.ts";
import type { QuestionsById } from "@/helpers/questionParts.ts";
import { IsAiTutorUserId } from "@/helpers/aiTutor.ts";
import { toast } from "sonner";

interface ChatMessagesProps {
  session?: AssignedSession;
  chat: Chat;
  chatMessage?: string;
  questionsById?: QuestionsById;
  shouldShowPreview?: boolean;
}

const Messages = styled.div`
  display: flex;
  flex-direction: column-reverse;
  flex: 1;

  padding: 1.5rem ${t.chatPaddingSides};
  gap: 0.75rem;

  content-visibility: visible;
  contain: size;

  border-bottom: ${t.brandBorderSegment};
  overflow-y: scroll;
  overflow-x: clip;
  scrollbar-width: none;

  overflow-anchor: none;
  overscroll-behavior: none;

  @container style(--small-height: 1) {
    gap: 0.2rem;
    padding: 0.5rem ${t.chatPaddingSides};
  }
`;

const ScrollMore = styled.p`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1rem;
  border-bottom: 2px dotted ${t.brandNeutralBlack};
`;

const PreviewText = styled.div`
  justify-self: flex-end;
  padding-bottom: 0.25rem;
  font-size: var(--14px);
  opacity: 0.75;
`;

// The number of messages to clip at before we stop rendering, it requires the user to scroll to the top before we render more
const MESSAGE_CLIP_AMOUNT = 35;

export const ChatMessages: FC<ChatMessagesProps> = ({
  session,
  chat,
  questionsById,
  shouldShowPreview,
}) => {
  const { inputMessage, file } = useInputStore();

  const [showAll, setShowAll] = useState<boolean>(
    !!sessionStorage.getItem(`linked-message-${chat.flowGeneratorSessionId}`),
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const flowSession = chat.flowGeneratorSession as FlowGeneratorSession;
  const messageCount =
    chat.newMessages.length + (flowSession?.flowGeneratorSession?.flowItems?.length ?? 0);
  const tryScrollToMessage = useRef<string>(null);

  useEffect(() => {
    if (chat.flowGeneratorSessionId) {
      tryScrollToMessage.current = null;
    }
  }, [chat.flowGeneratorSessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [messageCount]);

  // Don't want to reverse this every frame as this can be VERY large
  const { flowGenMessageAlwaysVisible, flowGenMessagesRest } = useMemo(() => {
    const messages = flowSession?.flowGeneratorSession?.flowItems?.slice().reverse();

    return {
      flowGenMessageAlwaysVisible: messages?.slice(0, MESSAGE_CLIP_AMOUNT),
      flowGenMessagesRest: messages?.slice(MESSAGE_CLIP_AMOUNT),
    };
  }, [flowSession?.flowGeneratorSession?.flowItems]);

  const scrollToMessage = useCallback(() => {
    const linkedMessageId = tryScrollToMessage.current;
    if (!tryScrollToMessage.current) return;

    const linkedMessage = document.getElementById(`flow-message-${linkedMessageId}`);

    if (!linkedMessage) {
      toast.warning(`Linked message ${linkedMessageId} not found`);
      console.log(flowSession?.flowGeneratorSession);
    } else {
      const scroll = () => {
        linkedMessage.scrollIntoView({
          inline: "center",
          block: "center",
          behavior: "instant",
        });

        if (!linkedMessage?.checkVisibility({ contentVisibilityAuto: true }))
          setTimeout(() => scroll(), 1);
      };

      scroll();

      linkedMessage.setAttribute("data-linked-message", "true");
    }

    sessionStorage.removeItem(`linked-message-${chat.flowGeneratorSessionId}`);
  }, [chat.flowGeneratorSessionId, flowSession?.flowGeneratorSession]);

  const handleScrollMessageLink = useCallback(() => {
    if (!flowSession || !flowSession.flowGeneratorSession?.flowItems?.length) return;

    const linkedMessageId = sessionStorage.getItem(`linked-message-${chat.flowGeneratorSessionId}`);

    if (!linkedMessageId) return;

    tryScrollToMessage.current = linkedMessageId;

    if (flowGenMessageAlwaysVisible.find((message) => message.flowItemId === linkedMessageId))
      setTimeout(() => scrollToMessage(), 1000);
  }, [scrollToMessage, flowGenMessageAlwaysVisible, chat.flowGeneratorSessionId, flowSession]);

  useEffect(() => {
    handleScrollMessageLink();
  }, [handleScrollMessageLink]);

  useEffect(() => {
    if (!showAll) return;

    setTimeout(() => {
      scrollToMessage();
    }, 100);
  }, [showAll, scrollToMessage]);

  const chatMessage = inputMessage.get(chat.flowGeneratorSessionId);
  const chatFile = file.get(chat.flowGeneratorSessionId);

  return (
    <Messages
      ref={scrollRef}
      onScroll={() => {
        const el = scrollRef.current;
        if (!el) return;

        const maxScrollDistance = el.scrollHeight - el.clientHeight;

        if (Math.abs(el.scrollTop) >= maxScrollDistance - 1) {
          setShowAll(true);
        }
      }}
    >
      <AnimatePresence initial={false}>
        {session &&
          !session.completed &&
          ((chatMessage && chatMessage !== "") || chatFile) &&
          shouldShowPreview && (
            <motion.div
              key="chat-preview-message"
              initial={{ height: 0 }}
              animate={{ height: "auto" }}
              exit={{ height: 0 }}
              transition={{
                height: { duration: 0.2, ease: "easeInOut" },
              }}
            >
              <motion.div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  flexDirection: "column",
                }}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 14 }}
                transition={{
                  opacity: { duration: 0.2, ease: "easeOut" },
                  y: { duration: 0.2, ease: "easeOut" },
                }}
              >
                <PreviewText>Message Preview</PreviewText>
                <FlowMessage
                  flowItem={{
                    flowItemId: "-1",
                    flowItemType: chatFile ? FlowItemType.PinnedImage : FlowItemType.Text,
                    text: chatMessage,
                    imageUrl: chatFile && URL.createObjectURL(chatFile),
                  }}
                  side={MessageSide.right}
                  options={{
                    name: null,
                  }}
                  bot={false}
                  preview={true}
                />
              </motion.div>
            </motion.div>
          )}
      </AnimatePresence>
      {chat?.newMessages.map((data) => {
        const isUser = data.userId === flowSession.userId;
        return (
          <FlowMessage
            key={data.flowItemId}
            flowItem={data}
            side={isUser ? MessageSide.left : MessageSide.right}
            options={{
              name: isUser ? flowSession.firstName : null,
            }}
            bot={data.userId === 0 || data.userId === -1}
            ai={IsAiTutorUserId(data.userId)}
            questionsById={questionsById}
          />
        );
      })}
      {flowGenMessageAlwaysVisible?.map((data) => {
        const isUser = data.userId === flowSession.userId;
        return (
          <FlowMessage
            key={data.flowItemId}
            flowItem={data}
            side={isUser ? MessageSide.left : MessageSide.right}
            options={{
              name: isUser ? flowSession.firstName : null,
            }}
            bot={data.userId === 0 || data.userId === -1}
            ai={IsAiTutorUserId(data.userId)}
            questionsById={questionsById}
          />
        );
      })}
      {flowGenMessagesRest?.length > 0 &&
        (showAll ? (
          flowGenMessagesRest?.map((data) => {
            const isUser = data.userId === flowSession.userId;
            return (
              <FlowMessage
                key={data.flowItemId}
                flowItem={data}
                side={isUser ? MessageSide.left : MessageSide.right}
                options={{
                  name: isUser ? flowSession.firstName : null,
                }}
                bot={data.userId === 0 || data.userId === -1}
                ai={IsAiTutorUserId(data.userId)}
                questionsById={questionsById}
              />
            );
          })
        ) : (
          <ScrollMore>Keep scrolling to load previous messages...</ScrollMore>
        ))}
    </Messages>
  );
};
